import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import heroImage from '../assets/faq/hero-questions.webp';

const SECTION_KEYS = ['general', 'pricing', 'process', 'ecommerce', 'technical'];

/**
 * One question.
 *
 * The open panel used max-h-[500px], which silently clipped any answer taller
 * than that. Grid-template-rows 0fr → 1fr animates to the content's real
 * height, so long answers can no longer be cut off mid-sentence.
 */
const FAQItem = ({ question, answer, i18nKey }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={`border-b border-gray-800 last:border-b-0 transition-colors ${isOpen ? 'bg-moss-night/40' : ''}`}>
            <button
                type="button"
                className="group flex w-full items-start justify-between gap-4 px-6 py-5 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-moss"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                <span className={`text-base font-medium leading-snug transition-colors duration-300 ${isOpen ? 'text-moss-light' : 'text-silver-light group-hover:text-white'}`}>
                    {question}
                </span>
                <FaChevronDown
                    aria-hidden="true"
                    className={`mt-1 flex-shrink-0 text-sm transition-all duration-300 ${isOpen ? 'rotate-180 text-moss-light' : 'text-silver-dark group-hover:text-silver-light'}`}
                />
            </button>

            <div
                className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
            >
                <div className="overflow-hidden">
                    <div className="whitespace-pre-wrap px-6 pb-6 pr-12 leading-relaxed text-silver-medium">
                        {i18nKey ? (
                            <Trans
                                i18nKey={i18nKey}
                                components={{
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans supplies anchor children from translation strings
                                    a: <a className="text-moss-light hover:text-moss underline" target="_blank" rel="noopener noreferrer" href="https://rivieratech.solutions" />
                                }}
                            />
                        ) : (
                            answer
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const FAQ = () => {
    const { t } = useTranslation();

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";

    // <PageHero /> renders immediately, so the hero needs no observer.
    const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    // Helper to get FAQ items from translation with keys
    const getFAQItems = (sectionKey) => {
        const items = t(`faq.sections.${sectionKey}.items`, { returnObjects: true });
        return Object.entries(items || {}).map(([key, value]) => ({
            ...value,
            i18nKey: `faq.sections.${sectionKey}.items.${key}.answer`
        }));
    };

    // Was five hand-copied blocks of identical markup. Driving them off
    // SECTION_KEYS means the sidebar nav and the panels can never disagree
    // about which sections exist.
    const sections = SECTION_KEYS.map((key) => ({
        key,
        title: t(`faq.sections.${key}.title`),
        items: getFAQItems(key),
    })).filter((s) => s.items.length > 0);

    const allFAQItems = sections.flatMap((s) => s.items);

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allFAQItems.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    };

    return (
        <div className="bg-black min-h-screen font-sans">
            <SEO
                title="Custom Web Development FAQ | Pricing, Timeline & Services"
                description="Frequently asked questions about custom websites, web application development, ecommerce website development, pricing, delivery timelines, and project scope. Clear answers for business owners planning a custom build."
                keywords="custom web development cost, custom web application developer for business, ecommerce website developer for small business, custom website FAQ, hire web developer FAQ, build custom business software web app"
                schema={schema}
                url="/faq"
            />

            <PageHero
                eyebrow={t('faq.hero.eyebrow', 'Questions')}
                title={t('faq.hero.title')}
                subtitle={t('faq.hero.subtitle')}
                ctaLabel={t('faq.hero.cta', 'Ask us directly')}
                ctaTo="/contact"
                backgroundImage={heroImage}
            />

            {/* Nineteen questions used to sit in one undifferentiated hairline
                ribbon in a max-w-3xl column, leaving most of the viewport empty
                and giving no way to reach a section without scrolling past every
                other one. Now: a sticky section index beside panelled sections. */}
            <section className="py-16 md:py-20 bg-black">
                <div className="container mx-auto px-4">
                    <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[15rem_1fr] lg:gap-14">

                        {/* Section index */}
                        <nav aria-label={t('faq.nav.label', 'FAQ sections')} className="lg:sticky lg:top-28 lg:self-start">
                            <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.2em] text-moss-light">
                                {t('faq.nav.title', 'Jump to')}
                            </p>
                            <ul className="flex flex-wrap gap-x-4 gap-y-2 lg:block lg:space-y-1">
                                {sections.map((section) => (
                                    <li key={section.key}>
                                        <a
                                            href={`#faq-${section.key}`}
                                            className="block border-l-2 border-transparent py-1.5 text-sm text-silver-medium transition-colors hover:border-moss hover:text-silver-light lg:pl-4"
                                        >
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 hidden rounded-lg border border-moss-deep/50 bg-moss-night p-5 lg:block">
                                <p className={`mb-2 text-sm font-medium ${primaryTextColor}`}>
                                    {t('faq.nav.asideTitle', 'Not covered here?')}
                                </p>
                                <p className={`mb-4 text-xs leading-relaxed ${secondaryTextColor}`}>
                                    {t('faq.nav.asideText', 'Ask directly and you will get a straight answer, not a sales call.')}
                                </p>
                                <Link
                                    to="/contact"
                                    className="text-sm font-medium text-moss-light transition-colors hover:text-moss"
                                >
                                    {t('faq.nav.asideCta', 'Get in touch →')}
                                </Link>
                            </div>
                        </nav>

                        {/* Sections */}
                        <div className="space-y-10">
                            {sections.map((section) => (
                                <div key={section.key} id={`faq-${section.key}`} className="scroll-mt-28">
                                    <h2 className={`mb-4 text-2xl font-serif font-medium ${primaryTextColor}`}>
                                        {section.title}
                                    </h2>
                                    <div className="overflow-hidden rounded-lg border border-gray-800 bg-off-black">
                                        {section.items.map((item, index) => (
                                            <FAQItem
                                                key={`${section.key}-${index}`}
                                                question={item.question}
                                                answer={item.answer}
                                                i18nKey={item.i18nKey}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section ref={ctaRef} className="py-20 bg-moss-night border-t border-moss-deep/40">
                <div className="container mx-auto px-4 text-center">
                    <div className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <h2 className={`text-2xl md:text-3xl font-serif font-medium mb-4 ${primaryTextColor}`}>
                            {t('faq.cta.title')}
                        </h2>
                        <p className={`${secondaryTextColor} mb-8`}>
                            {t('faq.cta.text')}
                        </p>
                        <Link
                            to="/contact"
                            className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                        >
                            {t('faq.cta.button')}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FAQ;
